// unternehmensverbund.ts — Regeln für den Abschnitt „Unternehmensverbund von
// <Firma>" unter der Kontaktsuche (User 28.09.2026): Sucht jemand eine Firma,
// sieht er darunter die Firmen aus ihrem Verbund und deren Unternehmensleitung,
// Gesellschafter und die übrige Verantwortung fürs Unternehmen (Steuerberater,
// Prokurist … — User 26.10.2026).
//
// Bewusst ohne React, damit die Regeln per Vitest prüfbar sind. Die Daten
// kommen aus GET /contact-relation/unternehmensverbund/:id — das Backend
// gruppiert (Leitung/Gesellschafter/Verantwortung) und filtert nach Leserechten.

import { rollenLabel } from "../../verantwortlichkeit/rollenRichtung";

export type VerbundFirma = {
  id: number;
  name: string | null;
  beziehung: string | null;
  anschrift: string | null;
};

export type VerbundRolle = {
  firmaId: number;
  firmaName: string | null;
  relationType: string;
  function: string | null;
  share: string | null;
  /** Ältere Server liefern das Feld nicht — dann gilt „aktiv". */
  ausgeschieden?: boolean;
  ende?: string | null;
};

export type VerbundInhaber = {
  id: number;
  name: string | null;
  typ: string | null;
  rollen: VerbundRolle[];
  /** Alle Rollen beendet → durchgestrichen. */
  ausgeschieden?: boolean;
};

export type Unternehmensverbund = {
  firma: { id: number; name: string | null } | null;
  firmen: VerbundFirma[];
  leitung: VerbundInhaber[];
  gesellschafter: VerbundInhaber[];
  /** Ältere Server liefern das Feld nicht — dann gibt es die Gruppe nicht. */
  verantwortung?: VerbundInhaber[];
};

/** Was die Kandidatenwahl von einer Tabellenzeile braucht. */
export type TrefferZeile = {
  kontaktId: number;
  typ?: string | number | null;
  displayName?: string | null;
};

/** Bis zu so vielen Treffern darf eine Firma die Liste „eindeutig dominieren". */
export const MAX_TREFFER_FUER_DOMINANZ = 10;

/**
 * Wie `kmvs_norm` im Backend: klein, Umlaute ausgeschrieben, Akzente weg —
 * „Müller" und „mueller" sind derselbe Name.
 */
export function normiere(s: string): string {
  return s
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

const tokens = (suchbegriff: string) =>
  normiere(suchbegriff)
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter(Boolean);

/** Für wen zeigt die Liste einen Umfeld-Abschnitt — und welchen? */
export type TrefferKandidat = { kontaktId: number; art: "firma" | "person" };

const istFirmaZeile = (r: TrefferZeile) => String(r.typ ?? "") === "Firma";
const istPersonZeile = (r: TrefferZeile) => String(r.typ ?? "") === "Person";

/** Ganze Wörter eines (normierten) Textes — Satzzeichen und Bindestriche trennen. */
const ganzeWoerter = (s: string) => normiere(s).split(/[^a-z0-9]+/).filter(Boolean);

/**
 * Für welchen Kontakt wird ein Umfeld-Abschnitt gezeigt? `null` = für keinen.
 * Eine Firma bekommt ihren Unternehmensverbund, eine Person ihren Haushalt
 * (User 28.09.2026) — dieselbe Regel für beide:
 *
 *  1. Genau EIN Treffer, und der ist eine Firma bzw. Person → dieser Kontakt.
 *  2. Wenige Treffer (≤ {@link MAX_TREFFER_FUER_DOMINANZ}, vollständig geladen)
 *     und genau EIN Kontakt der Art trägt jedes Suchwort im NAMEN → dieser.
 *     Die übrigen Treffer sind dann typischerweise sein Umfeld (derselbe Name
 *     in E-Mail-Domain, Arbeitgeber oder Adresse) — er dominiert eindeutig.
 *     Die Firma geht vor: dominiert eine Firma, bleibt es beim Verbund; eine
 *     Person dominiert nur, wenn keine Firma den Namen trägt.
 *     Tragen mehrere den Namen, entscheidet das GANZE Wort: „quip" steckt in
 *     „Quip GmbH" und in „e-quipment GmbH", als eigenes Wort aber nur in der
 *     ersten (PROD 28.09.2026 — vorher blieb der Verbund dort einfach weg).
 *  3. Mehr Treffer (oder nicht alles geladen), die Liste aber nach Relevanz
 *     sortiert (`relevanzSortiert`): Unter den geladenen Zeilen trägt genau EIN
 *     Kontakt jedes Suchwort als ganzes Wort im Namen → dieser. Die Tippfehler-
 *     Suche des Backends hängt an „quip gmbh" Dutzende andere GmbHs an; der
 *     eigentliche Treffer steht bei Relevanz-Sortierung trotzdem oben.
 *
 * Ohne Suchbegriff nie: Dann ist die Liste der ganze Bestand, keine Suche.
 */
export function trefferKandidat(opts: {
  suchbegriff: string;
  rows: TrefferZeile[];
  /** Gesamtzahl laut Backend; `null` = unbekannt (dann zählen die geladenen Zeilen). */
  total: number | null;
  hasMore: boolean;
  loading: boolean;
  /** Sortiert das Backend nach Relevanz (Suche ohne eigene Spaltensortierung)? */
  relevanzSortiert?: boolean;
}): TrefferKandidat | null {
  const worte = tokens(opts.suchbegriff);
  if (!worte.length || opts.loading) return null;
  const gesamt = opts.total ?? opts.rows.length;
  const alles = !opts.hasMore && opts.rows.length >= gesamt;

  const art = (r: TrefferZeile): TrefferKandidat["art"] | null =>
    istFirmaZeile(r) ? "firma" : istPersonZeile(r) ? "person" : null;
  const als = (r: TrefferZeile): TrefferKandidat | null => {
    const a = art(r);
    return a ? { kontaktId: r.kontaktId, art: a } : null;
  };

  if (alles && gesamt === 1 && opts.rows.length === 1) return als(opts.rows[0]);

  const suchWoerter = worte.flatMap(ganzeWoerter);
  const traegtName = (r: TrefferZeile) => {
    const name = normiere(r.displayName ?? "");
    return worte.every((w) => name.includes(w));
  };
  const traegtGanzeWoerter = (r: TrefferZeile) => {
    const imNamen = new Set(ganzeWoerter(r.displayName ?? ""));
    return suchWoerter.length > 0 && suchWoerter.every((w) => imNamen.has(w));
  };
  /** Genau einer — oder, bei mehreren, genau einer mit ganzen Wörtern. */
  const eindeutig = (kandidaten: TrefferZeile[]): TrefferZeile | null | "mehrdeutig" => {
    if (!kandidaten.length) return null;
    if (kandidaten.length === 1) return kandidaten[0];
    const ganz = kandidaten.filter(traegtGanzeWoerter);
    return ganz.length === 1 ? ganz[0] : "mehrdeutig";
  };

  if (alles && gesamt >= 2 && gesamt <= MAX_TREFFER_FUER_DOMINANZ) {
    const firma = eindeutig(opts.rows.filter((r) => istFirmaZeile(r) && traegtName(r)));
    if (firma === "mehrdeutig") return null;
    if (firma) return als(firma);
    const person = eindeutig(opts.rows.filter((r) => istPersonZeile(r) && traegtName(r)));
    return person && person !== "mehrdeutig" ? als(person) : null;
  }

  if (opts.relevanzSortiert && gesamt > 1) {
    const ganz = opts.rows.filter((r) => art(r) !== null && traegtGanzeWoerter(r));
    return ganz.length === 1 ? als(ganz[0]) : null;
  }
  return null;
}

/** Für welche Firma wird der Verbund gezeigt? `null` = für keine (s. {@link trefferKandidat}). */
export function verbundKandidat(opts: Parameters<typeof trefferKandidat>[0]): number | null {
  const k = trefferKandidat(opts);
  return k?.art === "firma" ? k.kontaktId : null;
}

/** Beziehung einer Verbundfirma aus Sicht der gesuchten Firma, als Anzeigetext. */
export function beziehungText(beziehung: string | null): string {
  switch (beziehung) {
    case "Tochterfirma":
      return "Tochterfirma";
    case "Mutterfirma":
      return "Mutterfirma";
    case "Schwester":
      return "Schwesterfirma";
    case "Beteiligung":
      return "Beteiligung";
    case "Joint Venture":
      return "Joint Venture";
    case null:
    case undefined:
    case "":
      return "mittelbar verbunden";
    default:
      return beziehung;
  }
}

/** Anteil als Text: reine Zahlen bekommen „ %", Bruchteile/Beträge bleiben wie erfasst. */
export function anteilText(share: string | null): string {
  const s = (share ?? "").trim();
  if (!s) return "";
  return /^\d+([.,]\d+)?$/.test(s) ? `${s.replace(".", ",")} %` : s;
}

/**
 * Rollen eines Inhabers in einer Zeile: „Geschäftsführer · Tochter GmbH",
 * mehrere mit „; ". Die Firma wird weggelassen, wenn es die gesuchte ist —
 * der Abschnitt heißt ja schon nach ihr.
 */
export function rollenText(e: VerbundInhaber, startId: number | null): string {
  return e.rollen
    .map((r) => {
      const rolle = rollenLabel(r.relationType, r.function);
      const anteil = anteilText(r.share);
      const teil = anteil ? `${rolle} (${anteil})` : rolle;
      return r.firmaId === startId || !r.firmaName ? teil : `${teil} · ${r.firmaName}`;
    })
    .join("; ");
}

/** „06/2024" aus „2024-06-30"; ohne Datum leer. */
export function endeText(ende: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})/.exec(ende ?? "");
  return m ? `${m[2]}/${m[1]}` : "";
}

/**
 * Die Rollen einzeln (für die Anzeige mit Durchstreichen): Text wie in
 * `rollenText`, dazu ob beendet und seit wann.
 */
export function rollenTeile(
  e: VerbundInhaber,
  startId: number | null,
  opts: { verantwortlichkeit?: boolean } = {},
): Array<{ text: string; ausgeschieden: boolean; ende: string }> {
  return e.rollen.map((r) => {
    // In der Gruppe „Verantwortung" IST die function die Rolle — auch bei
    // „Mensch im Unternehmen", das `rollenLabel` sonst zu „Mitarbeiter" macht.
    const rolle = (opts.verantwortlichkeit && r.function?.trim()) || rollenLabel(r.relationType, r.function);
    const anteil = anteilText(r.share);
    const teil = anteil ? `${rolle} (${anteil})` : rolle;
    return {
      text: r.firmaId === startId || !r.firmaName ? teil : `${teil} · ${r.firmaName}`,
      ausgeschieden: !!r.ausgeschieden,
      ende: endeText(r.ende),
    };
  });
}

/** Gibt es überhaupt etwas zu zeigen? Eine Firma ganz ohne Verbund bleibt ohne Abschnitt. */
export function hatInhalt(v: Unternehmensverbund | null): v is Unternehmensverbund {
  return (
    !!v?.firma &&
    v.firmen.length + v.leitung.length + v.gesellschafter.length + (v.verantwortung?.length ?? 0) > 0
  );
}
